"use client";

/**
 * CurrentTradeCard — the open signal suggestions. A position is one suggestion;
 * option positions can contain multiple contract legs.
 *
 * Positions arrive newest-first (sorted by the parent view). The newest fired
 * position gets a loud "FIRED <date>" tag so users instantly see the latest
 * signal when they land.
 *
 * Styling contract: white text only, emerald accent (no blue), minimal
 * rounding, tight padding, flat layout.
 */

import { useState, useEffect, useMemo } from "react";
import { ChevronLeft, ChevronRight, Zap, Loader2, AlertTriangle } from "lucide-react";
import type { CurrentPosition, PriceSource, TradeLeg, TradePosition } from "@/lib/mock/signal-performance";
import { SignalTradeChart, type DerivedLevels } from "./SignalTradeChart";
import { OptionPayoffChart } from "./OptionPayoffChart";
import { apiClient, type OptionsPnlAnalysis, type OptionsPnlSnapshot } from "@/lib/api-client";
import { PlotlyChart } from "@/components/notebook/PlotlyChart";

function fmt(n: number) {
  return n.toFixed(2);
}

function positionLabel(pos: TradePosition): string {
  return pos.name || pos.ticker;
}

function legLabel(leg: TradeLeg): string {
  if (leg.right) {
    return `${leg.ticker} ${leg.strike}${leg.right === "call" ? "C" : "P"}`;
  }
  return leg.ticker;
}

function calcPnlPct(pos: TradePosition, current: number, entry: number) {
  if (!Number.isFinite(current) || !Number.isFinite(entry) || entry === 0) return 0;
  if (pos.direction === "short") return ((entry - current) / entry) * 100;
  return ((current - entry) / entry) * 100;
}

function formatRiskReward(n?: number) {
  if (n == null) return "Unlimited";
  return `$${n.toLocaleString()}`;
}

function Level({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: "entry" | "target" | "stop" | "now";
}) {
  const color =
    tone === "target"
      ? "text-emerald-300"
      : tone === "stop"
        ? "text-red-300"
        : "text-white";
  return (
    <div className="rounded-sm border border-white/10 bg-black/40 px-3 py-2.5 flex flex-col gap-0.5">
      <span className="text-[10px] font-bold uppercase tracking-widest text-white/70">
        {label}
      </span>
      <span className={`text-sm font-bold tabular-nums ${color}`}>{value}</span>
    </div>
  );
}

function ymd(d: Date) {
  return d.toISOString().split("T")[0];
}

function fmtDay(iso?: string) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export function CurrentTradeCard({
  position,
  isMock = false,
}: {
  position: CurrentPosition;
  isMock?: boolean;
}) {
  // Empty-state guard: a signal can be active but currently holding nothing
  // (e.g. a run that registered 0 positions). Without this, the code below
  // reads position.positions[0].kind on undefined and white-screens the page.
  if (!position?.positions?.length) {
    return (
      <div className="rounded-sm border border-white/10 bg-black/40 px-5 py-6 text-center">
        <p className="text-sm font-bold text-white">No open positions right now</p>
        <p className="mt-1.5 text-[13px] text-white/60 leading-relaxed">
          This signal is active and waiting for its next setup. New trades appear
          here the moment they fire.
        </p>
      </div>
    );
  }

  const multi = position.positions.length > 1;

  const [activeIndex, setActiveIndex] = useState(0);
  const activePos = position.positions[activeIndex];
  const isOption = activePos.kind === "option";
  // Net-credit strategy: entry = credit received, current = cost to close.
  const isCredit = isOption && !!activePos.is_credit;

  // Newest fired calendar day across positions — positions opened on that day
  // get the "fired most recently" treatment.
  const newestDay = useMemo(() => {
    let max = "";
    for (const p of position.positions) {
      const day = (p.opened_at ?? "").slice(0, 10);
      if (day > max) max = day;
    }
    return max;
  }, [position.positions]);
  const isNewest = (activePos.opened_at ?? "").slice(0, 10) === newestDay && !!newestDay;

  // In mock mode the chart derives real levels and hands them back so the
  // displayed equity numbers match the chart. Options keep premium values.
  const [derived, setDerived] = useState<DerivedLevels | null>(null);

  // Reset derived levels when switching positions in the carousel
  useEffect(() => {
    setDerived(null);
  }, [activeIndex]);

  const tickers = useMemo(
    () => Array.from(
      new Set(
        position.positions
          .filter((p) => p.kind === "equity")
          .map((p) => p.underlying_ticker || p.ticker)
          .filter(Boolean),
      ),
    ),
    [position.positions],
  );
  const tickerKey = tickers.join("|");
  const [liveCurrentByTicker, setLiveCurrentByTicker] = useState<Record<string, number>>({});

  // Current open-position marks should match Polygon, not the stale seeded mark.
  // Entry/target/stop stay fixed from the registered signal record.
  useEffect(() => {
    if (!tickerKey) return;
    let cancelled = false;
    const end = new Date();
    const start = new Date(end.getTime() - 5 * 86_400_000);

    Promise.all(
      tickers.map(async (ticker) => {
        const url = `/api/polygon/aggregates?ticker=${encodeURIComponent(
          ticker,
        )}&from=${ymd(start)}&to=${ymd(end)}&timespan=hour&multiplier=1`;
        const res = await fetch(url);
        const json = await res.json();
        const last = Array.isArray(json.results) ? json.results.at(-1) : null;
        const close = typeof last?.c === "number" ? last.c : null;
        return [ticker, close] as const;
      }),
    )
      .then((pairs) => {
        if (cancelled) return;
        setLiveCurrentByTicker((prev) => {
          const next = { ...prev };
          for (const [ticker, close] of pairs) {
            if (close != null) next[ticker] = close;
          }
          return next;
        });
      })
      .catch(() => {
        // Keep the backend mark if Polygon is temporarily unavailable.
      });

    return () => {
      cancelled = true;
    };
  }, [tickerKey, tickers]);

  const [markSnapshot, setMarkSnapshot] = useState<OptionsPnlSnapshot | null>(null);
  const [markLoading, setMarkLoading] = useState(false);

  const markKey = useMemo(
    () =>
      isOption
        ? activePos.legs
            .map((l) => `${l.polygon_ticker ?? l.ticker}:${l.entry}:${l.current_price}:${l.expiry}`)
            .join("|")
        : "",
    [activePos.legs, isOption],
  );

  useEffect(() => {
    if (!isOption || isMock || !markKey) {
      setMarkSnapshot(null);
      setMarkLoading(false);
      return;
    }
    let cancelled = false;
    setMarkSnapshot(null);
    setMarkLoading(true);
    apiClient
      .getOptionsPnlSnapshot({
        legs: activePos.legs.map((l) => ({
          ...l,
          polygon_ticker: l.polygon_ticker,
          entry: l.entry,
          exit_price: l.current_price,
        })),
        entry_at: activePos.opened_at ?? position.fired_at,
        direction: activePos.direction,
        is_credit: !!activePos.is_credit,
        notional_usd: activePos.notional_usd,
      })
      .then((res) => {
        if (!cancelled) setMarkSnapshot(res);
      })
      .catch(() => {
        if (!cancelled) setMarkSnapshot(null);
      })
      .finally(() => {
        if (!cancelled) setMarkLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeIndex, activePos, isOption, isMock, markKey, position.fired_at]);

  const displayLegs = useMemo(
    () =>
      activePos.legs.map((leg, i) => {
        const snap = markSnapshot?.legs?.[i];
        return {
          ...leg,
          entry: typeof snap?.entry === "number" ? snap.entry : leg.entry,
          current_price: typeof snap?.exit === "number" ? snap.exit : leg.current_price,
          bid: typeof snap?.exit_bid === "number" ? snap.exit_bid : leg.bid,
          ask: typeof snap?.exit_ask === "number" ? snap.exit_ask : leg.ask,
          price_source: (snap?.exit_source as PriceSource | undefined) ?? leg.price_source,
        };
      }),
    [activePos.legs, markSnapshot],
  );

  const chartTicker = activePos.underlying_ticker || activePos.ticker;
  const entry = isMock && !isOption && derived?.entry
    ? derived.entry
    : isOption && typeof markSnapshot?.entry === "number"
      ? markSnapshot.entry
      : activePos.entry;
  const current =
    !isOption
      ? liveCurrentByTicker[chartTicker] ?? (isMock && derived?.current ? derived.current : activePos.current_price)
      : typeof markSnapshot?.exit === "number"
        ? markSnapshot.exit
        : activePos.current_price;
  const target =
    isMock && !isOption && derived?.target
      ? derived.target
      : activePos.exit.take_profit ?? (isCredit ? entry * 0.2 : entry * 1.1);

  // Trailing stop: live stop level = running peak × (1 − trail_pct). Only
  // ever ratchets in the position's favor. Fixed stop_loss wins if both set.
  const trailPct = activePos.exit.trail_pct;
  const trailingStop =
    trailPct != null
      ? (activePos.peak_price ?? Math.max(entry, current)) *
        (activePos.direction === "short" ? 1 + trailPct : 1 - trailPct)
      : null;
  const stop =
    isMock && !isOption && derived?.stop
      ? derived.stop
      : activePos.exit.stop_loss ?? trailingStop ?? (isCredit ? entry * 2 : entry * 0.95);
  const stopLabel =
    activePos.exit.stop_loss == null && trailingStop != null
      ? `Trail stop (${Math.round(trailPct! * 100)}%)`
      : isOption
        ? "Stop value"
        : "Stop";

  const pnlPct = calcPnlPct(activePos, current, entry);
  const pnlUp = pnlPct >= 0;

  const entryTs = Math.floor(
    new Date(activePos.opened_at ?? position.fired_at).getTime() / 1000,
  );
  const chartToSec = useMemo(
    () => Math.floor(Date.now() / 1000),
    [activePos.id, activePos.opened_at],
  );
  const referenceLines = useMemo(
    () => isOption
      ? [
        ...activePos.legs
          .filter((leg) => leg.strike != null)
          .map((leg) => ({
            price: leg.strike as number,
            title: `${leg.action === "sell" ? "Sell" : "Buy"} ${leg.strike}${leg.right === "call" ? "C" : "P"}`,
            color: leg.action === "sell" ? "#f97316" : "#34d399",
          })),
        ...(activePos.breakeven
          ? [{ price: activePos.breakeven, title: "Breakeven", color: "#eab308" }]
          : []),
      ]
      : undefined,
    [activePos, isOption],
  );

  // Rich option P&L analysis (optionlab via backend → PKA). Returns the P&L
  // diagram (with date slider) + multi-expiry heatmap as Plotly figures.
  // Skipped in mock mode (no backend). Re-fetches when the active option
  // position's legs change.
  const [pnl, setPnl] = useState<OptionsPnlAnalysis | null>(null);
  const [pnlLoading, setPnlLoading] = useState(false);
  const [pnlError, setPnlError] = useState<string | null>(null);

  const legsKey = useMemo(
    () =>
      isOption
        ? displayLegs
            .map((l) => `${l.right}:${l.strike}:${l.action}:${l.quantity ?? 1}:${l.expiry}:${l.entry}`)
            .join("|")
        : "",
    [displayLegs, isOption],
  );
  const underlying = activePos.underlying_ticker || activePos.ticker;

  useEffect(() => {
    if (!isOption || isMock || !legsKey) {
      setPnl(null);
      setPnlError(null);
      setPnlLoading(false);
      return;
    }
    let cancelled = false;
    setPnl(null);
    setPnlLoading(true);
    setPnlError(null);
    apiClient
      .getOptionsPnlAnalysis({
        underlying,
        title: positionLabel(activePos),
        legs: displayLegs.map((l) => ({
          right: l.right,
          strike: l.strike,
          // The payoff curve is anchored to the position's ENTRY cost basis (what
          // we actually opened the trade at), NOT the live mark. The live state is
          // already shown by the "Current" spot marker + the Cost-to-Close header,
          // so the curve must not be rebuilt off the moving mark.
          current_price: l.entry,
          action: l.action,
          quantity: l.quantity ?? 1,
          expiry: l.expiry,
        })),
      })
      .then((res) => {
        if (!cancelled) setPnl(res);
      })
      .catch((e) => {
        if (!cancelled) setPnlError(e instanceof Error ? e.message : "P&L analysis unavailable");
      })
      .finally(() => {
        if (!cancelled) setPnlLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOption, isMock, legsKey, underlying, activeIndex, displayLegs, activePos]);

  return (
    <div className="rounded-sm border border-white/10 bg-black/40 p-4">
      {multi && (
        <div className="flex items-center justify-between mb-4 pb-3 border-b border-white/10">
          <h3 className="text-[10px] font-bold text-white/80 uppercase tracking-widest">
            Open Positions ({position.positions.length})
          </h3>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setActiveIndex((i) => Math.max(0, i - 1))}
              disabled={activeIndex === 0}
              className="p-1.5 hover:bg-white/10 rounded-sm disabled:opacity-30 transition-colors text-white"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="text-xs font-bold text-white tabular-nums">
              {activeIndex + 1} / {position.positions.length}
            </span>
            <button
              onClick={() => setActiveIndex((i) => Math.min(position.positions.length - 1, i + 1))}
              disabled={activeIndex === position.positions.length - 1}
              className="p-1.5 hover:bg-white/10 rounded-sm disabled:opacity-30 transition-colors text-white"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-xl font-bold text-white tracking-tight">{positionLabel(activePos)}</span>
          <span
            className={`px-2 py-0.5 rounded-sm text-[10.5px] font-bold uppercase tracking-wider border ${
              activePos.direction === "long"
                ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/30"
                : "bg-red-500/20 text-red-300 border-red-500/30"
            }`}
          >
            {activePos.direction}
          </span>
          {isOption && (
            <span className="px-2 py-0.5 rounded-sm text-[10.5px] font-bold bg-emerald-500/15 border border-emerald-500/30 text-emerald-300">
              {activePos.strategy_type} · {activePos.expiry}
            </span>
          )}
          {/* Fired tag — inline, carries the exact date (single source of truth) */}
          {isNewest && (
            <span className="inline-flex items-center gap-1.5 rounded-sm border border-emerald-400/40 bg-emerald-500/15 px-2.5 py-0.5">
              <Zap className="w-3.5 h-3.5 text-emerald-300" />
              <span className="text-[10.5px] font-bold uppercase tracking-widest text-emerald-300">
                Entered
              </span>
              {fmtDay(activePos.opened_at) && (
                <span className="text-[11px] font-bold text-white tabular-nums">
                  {fmtDay(activePos.opened_at)}
                </span>
              )}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {activePos.notional_usd != null && activePos.notional_usd > 0 && (
            <span className="px-2 py-0.5 rounded-sm text-[10.5px] font-bold tabular-nums bg-white/5 border border-white/15 text-white">
              ${Math.round(activePos.notional_usd).toLocaleString()} deployed
            </span>
          )}
          <span className={`text-lg font-bold tabular-nums ${pnlUp ? "text-emerald-400" : "text-red-400"}`}>
            {pnlUp ? "+" : ""}
            {pnlPct.toFixed(1)}%
          </span>
        </div>
      </div>

      {activePos.illiquid && (
        <div className="mb-4 flex items-start gap-2.5 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2.5">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
          <div className="text-[12px] leading-snug text-amber-200/90">
            <span className="font-bold text-amber-300">Hard to fill right now.</span>{" "}
            {activePos.illiquid_reason ??
              `One option leg has very low open interest${
                activePos.worst_leg_oi != null ? ` (${activePos.worst_leg_oi} contracts)` : ""
              } and a wide bid/ask spread, so you may not get filled near the quoted price. Shown for transparency; the signal now blocks new trades like this.`}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5 mb-4">
        <Level
          label={isCredit ? "Credit received" : isOption ? "Net entry" : "Entry"}
          value={`$${fmt(entry)}`}
          tone="entry"
        />
        <Level
          label={isCredit ? "Cost to close" : isOption ? "Current value" : "Current"}
          value={`$${fmt(current)}`}
          tone="now"
        />
        <Level
          label={isCredit ? "Target cost" : isOption ? "Target value" : "Target"}
          value={`$${fmt(target)}`}
          tone="target"
        />
        <Level label={stopLabel} value={`$${fmt(stop)}`} tone="stop" />
        <Level
          label={isOption ? "DTE" : "Time exit"}
          value={isOption && activePos.days_to_expiry ? `${activePos.days_to_expiry}d` : activePos.exit.time_exit_days ? `${activePos.exit.time_exit_days}d` : "—"}
          tone="entry"
        />
      </div>

      {isOption && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mb-4">
          <Level label="Max risk" value={formatRiskReward(activePos.max_risk)} tone="stop" />
          <Level label="Max reward" value={formatRiskReward(activePos.max_reward)} tone="target" />
          <Level label="Breakeven" value={activePos.breakeven ? `$${fmt(activePos.breakeven)}` : "—"} tone="now" />
          <Level label="Contracts" value={`${activePos.legs.length}`} tone="entry" />
        </div>
      )}

      {/* ===== OPTION layout: lead with the legs table, then payoff, then the
           option-contract price chart, with the underlying chart demoted last. */}
      {isOption && (
        <div className="mb-4 overflow-x-auto rounded-sm border border-white/10">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="text-[10px] font-bold uppercase tracking-wider text-white/80 bg-black/60">
                <th className="text-left px-3 py-2">Leg</th>
                <th className="text-left px-3 py-2">Action</th>
                <th className="text-right px-3 py-2">Entry</th>
                <th className="text-right px-3 py-2">Current</th>
                <th className="text-right px-3 py-2">Bid / Ask</th>
                <th className="text-right px-3 py-2">Volume / OI</th>
              </tr>
            </thead>
            <tbody className="bg-black/20">
              {displayLegs.map((leg, i) => (
                <tr key={`${leg.polygon_ticker ?? leg.ticker}-${i}`} className={i % 2 ? "bg-white/[0.02]" : ""}>
                  <td className="px-3 py-2 font-bold text-white">{legLabel(leg)}</td>
                  <td className="px-3 py-2">
                    <span
                      className={`rounded-sm border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                        leg.action === "sell"
                          ? "border-orange-500/30 bg-orange-500/15 text-orange-300"
                          : "border-emerald-500/30 bg-emerald-500/15 text-emerald-300"
                      }`}
                    >
                      {leg.action ?? "buy"} {leg.quantity ?? 1}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-white font-medium">${fmt(leg.entry)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-emerald-400 font-bold">${fmt(leg.current_price)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-white font-medium">
                    {markLoading && leg.bid == null && leg.ask == null
                      ? "loading…"
                      : `${leg.bid != null ? `$${fmt(leg.bid)}` : "—"} / ${leg.ask != null ? `$${fmt(leg.ask)}` : "—"}`}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-white font-medium">
                    {leg.volume?.toLocaleString() ?? "—"} / {leg.open_interest?.toLocaleString() ?? "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* P&L analysis — rich optionlab P&L diagram (date slider) + multi-expiry
          heatmap + strategy metrics. Falls back to the lightweight JS payoff
          tent while loading, on error, or in mock mode (no backend). */}
      {isOption && (
        <div className="mb-4">
          {pnl?.strategy_metrics && (
            <div className="mb-3 grid grid-cols-2 sm:grid-cols-4 gap-2.5">
              <Level
                label="Net cost"
                value={`$${Math.abs(pnl.strategy_metrics.net_cost).toLocaleString(undefined, { maximumFractionDigits: 0 })}${pnl.strategy_metrics.net_cost < 0 ? " cr" : ""}`}
                tone="entry"
              />
              <Level
                label="Max profit"
                value={`$${pnl.strategy_metrics.max_profit.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
                tone="target"
              />
              <Level
                label="Max loss"
                value={`$${pnl.strategy_metrics.max_loss.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
                tone="stop"
              />
              <Level
                label="Prob. of profit"
                value={`${Math.round(pnl.strategy_metrics.probability_of_profit * 100)}%`}
                tone="now"
              />
            </div>
          )}

          {pnlLoading && !pnl && (
            <div className="rounded-sm border border-white/10 bg-black/40 px-4 py-5">
              <div className="flex items-center gap-2 text-[12px] font-semibold text-white/70">
                <Loader2 className="w-4 h-4 animate-spin text-emerald-300" />
                Computing fresh P&amp;L diagram and heatmap for {positionLabel(activePos)}…
              </div>
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className="h-12 animate-pulse rounded-sm bg-white/[0.06]" />
                ))}
              </div>
              <div className="mt-4 h-[360px] animate-pulse rounded-sm bg-white/[0.04]" />
              <div className="mt-3 h-[300px] animate-pulse rounded-sm bg-white/[0.035]" />
            </div>
          )}

          {pnl?.pnl_diagram_figure && (
            <div key={`${legsKey}-pnl`} className="mb-3 rounded-sm border border-white/10 bg-black/40 overflow-hidden">
              <PlotlyChart data={pnl.pnl_diagram_figure} noBorder />
            </div>
          )}
          {pnl?.heatmap_figure && (
            <div key={`${legsKey}-heat`} className="rounded-sm border border-white/10 bg-black/40 overflow-hidden">
              <PlotlyChart data={pnl.heatmap_figure} noBorder />
            </div>
          )}

          {/* Fallback payoff tent: shown when the rich analysis isn't available
              (mock mode, still loading with nothing yet, or backend error). */}
          {!pnl?.pnl_diagram_figure && !pnlLoading && (
            <>
              {pnlError && (
                <p className="mb-2 text-[11px] text-white/50">
                  Showing the quick payoff view — full P&amp;L analysis unavailable right now.
                </p>
              )}
              <OptionPayoffChart legs={displayLegs} />
            </>
          )}
        </div>
      )}

      {/* Option-contract price chart(s) — the actual contract, one per leg */}
      {isOption &&
        displayLegs.map((leg, i) => {
          const occ = leg.polygon_ticker ?? leg.ticker;
          if (!occ) return null;
          return (
            <div className="mb-4" key={`contract-${occ}-${i}`}>
              <SignalTradeChart
                ticker={occ}
                label={`${legLabel(leg)} contract`}
                direction={leg.action === "sell" ? "short" : "long"}
                entryTs={entryTs}
                entry={leg.entry}
                underlyingTicker={leg.underlying_ticker ?? leg.ticker}
                rangeSelector
                showTradeLevels={false}
                timespan="hour"
                seriesType="line"
                referenceLines={[{ price: leg.entry, title: "Entry", color: "#ffffff" }]}
                height={300}
              />
            </div>
          );
        })}

      {/* Underlying price chart — primary for equities, demoted context for options */}
      <div>
        {isOption && (
          <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-white/60">
            Underlying — {chartTicker} (context)
          </div>
        )}
        <SignalTradeChart
          key={chartTicker}
          ticker={chartTicker}
          label={positionLabel(activePos)}
          direction={activePos.direction}
          entryTs={entryTs}
          entry={entry}
          target={target}
          stop={stop}
          autoFit={isMock && !isOption}
          onLevels={isMock && !isOption ? setDerived : undefined}
          toSec={chartToSec}
          tradeRangeSelector
          showTradeLevels={!isOption}
          referenceLines={referenceLines}
          height={360}
        />
      </div>

      {multi && !isOption && (
        <div className="mt-4 overflow-x-auto rounded-sm border border-white/10">
          <table className="w-full text-sm min-w-[480px]">
            <thead>
              <tr className="text-[10px] font-bold uppercase tracking-wider text-white/80 bg-black/60">
                <th className="text-left px-3 py-2">Position</th>
                <th className="text-left px-3 py-2">Dir</th>
                <th className="text-right px-3 py-2">Added</th>
                <th className="text-right px-3 py-2">Entry</th>
                <th className="text-right px-3 py-2">Current</th>
                <th className="text-right px-3 py-2">TP / SL</th>
                <th className="text-right px-3 py-2">PnL</th>
              </tr>
            </thead>
            <tbody className="bg-black/20">
              {position.positions.map((pos, i) => {
                const posTicker = pos.underlying_ticker || pos.ticker;
                const currentPrice =
                  pos.kind === "equity"
                    ? liveCurrentByTicker[posTicker] ?? pos.current_price
                    : pos.current_price;
                const lp = calcPnlPct(pos, currentPrice, pos.entry);
                const up = lp >= 0;
                const isActive = i === activeIndex;
                return (
                  <tr
                    key={`${pos.ticker}-${i}`}
                    onClick={() => setActiveIndex(i)}
                    className={`transition-colors cursor-pointer ${
                      isActive ? "bg-emerald-500/10" : "hover:bg-white/[0.04]"
                    } ${i % 2 && !isActive ? "bg-white/[0.01]" : ""}`}
                  >
                    <td className="px-3 py-2 font-bold text-white flex items-center gap-2">
                      {isActive && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />}
                      {!isActive && <span className="w-1.5 h-1.5" />}
                      {positionLabel(pos)}
                    </td>
                    <td className="px-3 py-2 text-white capitalize font-medium">{pos.direction}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-white font-medium whitespace-nowrap">
                      {fmtDay(pos.opened_at) ?? "—"}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-white font-medium">
                      ${fmt(pos.entry)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-emerald-400 font-bold">
                      ${fmt(currentPrice)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-white font-medium">
                      {pos.exit.take_profit ? `$${fmt(pos.exit.take_profit)}` : "—"} /{" "}
                      {pos.exit.stop_loss
                        ? `$${fmt(pos.exit.stop_loss)}`
                        : pos.exit.trail_pct
                          ? `${Math.round(pos.exit.trail_pct * 100)}% trail`
                          : "—"}
                    </td>
                    <td className={`px-3 py-2 text-right tabular-nums font-bold ${up ? "text-emerald-400" : "text-red-400"}`}>
                      {up ? "+" : ""}
                      {lp.toFixed(1)}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
